import { useLocation, useNavigate } from "react-router-dom";
import { Building2, Sparkles, Layers, Shuffle, Database } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRole } from "@/hooks/useRole";
import { ChangelogPanel } from "@/components/scratchpad/ChangelogPanel";

export function AdminSidebar() {
  const { isAdmin, isLoading } = useRole();
  const location = useLocation();
  const navigate = useNavigate();

  if (isLoading || !isAdmin) return null;

  return (
    <aside className="w-[220px] flex-shrink-0 sticky top-0 h-screen bg-black/60 border-r border-white/[0.06] flex flex-col overflow-y-auto backdrop-blur-xl">
      {/* Main nav */}
      <nav className="flex-1 px-2 py-3 space-y-0.5">
        {/* Scratchpad section */}
        <div className="pt-2 pb-1 px-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30">
            Scratchpad
          </p>
        </div>

        <NavItem
          icon={<Sparkles className="w-4 h-4" />}
          label="Agent Builder"
          active={location.pathname === "/scratchpad"}
          onClick={() => navigate("/scratchpad")}
        />

        <NavItem
          icon={<Shuffle className="w-4 h-4" />}
          label="Transformer"
          active={location.pathname === "/transforms"}
          onClick={() => navigate("/transforms")}
        />

        <NavItem
          icon={<Layers className="w-4 h-4" />}
          label="Ontology"
          active={location.pathname === "/ontology"}
          onClick={() => navigate("/ontology")}
        />

        <NavItem
          icon={<Database className="w-4 h-4" />}
          label="Data Sources"
          active={location.pathname === "/data-sources"}
          onClick={() => navigate("/data-sources")}
        />
      </nav>

      {/* Changelog — collapsible, collapsed by default */}
      <div className="px-2 pb-2">
        <ChangelogPanel />
      </div>

      {/* Admin badge */}
      <div className="px-4 py-3 border-t border-white/[0.06]">
        <div className="flex items-center gap-1.5">
          <Building2 className="w-3 h-3 text-white/20" />
          <span className="text-[10px] text-white/25 font-medium">Admin view</span>
        </div>
      </div>
    </aside>
  );
}

function NavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors",
        active
          ? "bg-white/[0.08] text-white"
          : "text-white/50 hover:text-white/80 hover:bg-white/[0.05]"
      )}
    >
      <span className={cn("flex-shrink-0", active ? "text-white" : "text-white/40")}>{icon}</span>
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}
